import { createClient } from '@/lib/supabase/client';
import { auditService } from './auditService';

export type StoreStatus = 'active' | 'suspended' | 'trial' | 'expired';
export type StorePlan = 'starter' | 'professional' | 'enterprise';

export interface EfStore {
  id: string;
  store_name: string;
  store_slug: string;
  owner_name: string;
  owner_email: string;
  owner_phone: string;
  company_name: string;
  city: string;
  country: string;
  store_address?: string;
  plan: StorePlan;
  store_status: StoreStatus;
  trial_ends_at: string | null;
  subscription_ends_at: string | null;
  auth_user_id: string | null;
  notes: string;
  created_at: string;
  updated_at: string;
}

// ── Store Features ────────────────────────────────────────────────────────────

export interface StoreFeature {
  feature_key: string;
  enabled: boolean;
}

export const ALL_FEATURES: { key: string; labelFr: string; labelEn: string; plans: StorePlan[] }[] = [
  { key: 'pos',                 labelFr: 'Point de vente (POS)',             labelEn: 'Point of Sale (POS)',          plans: ['starter', 'professional', 'enterprise'] },
  { key: 'inventory',           labelFr: 'Inventaire & Produits',            labelEn: 'Inventory & Products',         plans: ['starter', 'professional', 'enterprise'] },
  { key: 'clients',             labelFr: 'Gestion des clients',              labelEn: 'Client Management',            plans: ['starter', 'professional', 'enterprise'] },
  { key: 'suppliers',           labelFr: 'Fournisseurs',                     labelEn: 'Suppliers',                    plans: ['professional', 'enterprise'] },
  { key: 'sales',               labelFr: 'Ventes & Analytique',              labelEn: 'Sales & Analytics',            plans: ['professional', 'enterprise'] },
  { key: 'reports',             labelFr: 'Rapports',                         labelEn: 'Reports',                      plans: ['professional', 'enterprise'] },
  { key: 'settings',            labelFr: 'Paramètres',                       labelEn: 'Settings',                     plans: ['starter', 'professional', 'enterprise'] },
  { key: 'administration',      labelFr: 'Administration équipe',            labelEn: 'Team Administration',          plans: ['enterprise'] },
  { key: 'expenses',            labelFr: 'Gestion des dépenses',             labelEn: 'Expense Management',           plans: ['professional', 'enterprise'] },
  { key: 'supplier_reports',    labelFr: 'Rapports fournisseurs',            labelEn: 'Supplier Reports',             plans: ['professional', 'enterprise'] },
  { key: 'client_transactions', labelFr: 'Transactions clients',             labelEn: 'Client Transactions',          plans: ['professional', 'enterprise'] },
  { key: 'categories',          labelFr: 'Catégories',                       labelEn: 'Categories',                   plans: ['starter', 'professional', 'enterprise'] },
  { key: 'barcode',             labelFr: 'Codes-barres',                     labelEn: 'Barcodes',                     plans: ['professional', 'enterprise'] },
  { key: 'troc',                labelFr: 'Mode Troc',                        labelEn: 'Barter Mode',                  plans: ['enterprise'] },
  { key: 'tva',                 labelFr: 'TVA (Taxe sur la valeur ajoutée)', labelEn: 'VAT (Value Added Tax)',         plans: ['professional', 'enterprise'] },
  { key: 'discounts',           labelFr: 'Remises & Réductions',             labelEn: 'Discounts & Reductions',       plans: ['professional', 'enterprise'] },
  { key: 'messaging',           labelFr: 'E-easy Messagerie',                labelEn: 'E-easy Messaging',             plans: ['starter', 'professional', 'enterprise'] },
  { key: 'invoice_repository',  labelFr: 'Répertoire des factures',          labelEn: 'Invoice Repository',           plans: ['professional', 'enterprise'] },
  { key: 'payments',            labelFr: 'Paiements & Créances',             labelEn: 'Payments & Receivables',       plans: ['professional', 'enterprise'] },
  { key: 'proforma',            labelFr: 'Factures Proforma',                labelEn: 'Proforma Invoices',            plans: ['professional', 'enterprise'] },
];

const supabase = createClient();

export async function fetchStoreFeatures(storeId: string): Promise<StoreFeature[]> {
  const { data, error } = await supabase
    .from('ef_store_features')
    .select('feature_key, enabled')
    .eq('store_id', storeId);
  if (error) throw error;
  return data ?? [];
}

export async function saveStoreFeatures(storeId: string, features: StoreFeature[]): Promise<void> {
  const rows = features.map((f) => ({
    store_id: storeId,
    feature_key: f.feature_key,
    enabled: f.enabled,
    updated_at: new Date().toISOString(),
  }));
  const { error } = await supabase
    .from('ef_store_features')
    .upsert(rows, { onConflict: 'store_id,feature_key' });
  if (error) throw error;
  auditService.log(
    'store_features_update',
    `Fonctionnalités mises à jour pour boutique ${storeId}: ${features.filter((f) => f.enabled).map((f) => f.feature_key).join(', ')}`
  );
}

export interface CreateStorePayload {
  store_name: string;
  store_slug: string;
  owner_name: string;
  owner_email: string;
  owner_phone: string;
  company_name: string;
  city: string;
  country: string;
  store_address?: string;
  plan: StorePlan;
  store_status: StoreStatus;
  trial_ends_at?: string | null;
  subscription_ends_at?: string | null;
  notes?: string;
}

export async function fetchStores(): Promise<EfStore[]> {
  const { data, error } = await supabase
    .from('ef_stores')
    .select('*')
    .order('created_at', { ascending: false });
  if (error) throw error;
  return data ?? [];
}

export async function createStore(payload: CreateStorePayload): Promise<EfStore> {
  // Use the dedicated Edge Function with service role key — completely bypasses RLS
  const supabaseUrl = process.env.NEXT_PUBLIC_SUPABASE_URL;
  if (!supabaseUrl) throw new Error('NEXT_PUBLIC_SUPABASE_URL is not set');

  // Get the current session token to authenticate the Edge Function call
  const { data: { session }, error: sessionError } = await supabase.auth.getSession();

  if (sessionError || !session?.access_token) {
    throw new Error('Vous devez être connecté pour créer une boutique. Veuillez vous reconnecter.');
  }

  // ── Attempt 1: Edge Function (uses service role, bypasses RLS) ──────────
  try {
    const response = await fetch(`${supabaseUrl}/functions/v1/create-store`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${session.access_token}`,
        'apikey': process.env.NEXT_PUBLIC_SUPABASE_ANON_KEY ?? '',
      },
      body: JSON.stringify(payload),
    });

    const result = await response.json();

    if (response.ok) {
      auditService.log(
        'store_create',
        `Boutique créée: ${payload.store_name} (${payload.store_slug}) — Propriétaire: ${payload.owner_name} <${payload.owner_email}> — Plan: ${payload.plan}`
      );
      return result.store as EfStore;
    }

    // If it's not a role/auth error, surface it immediately
    if (response.status !== 403 && response.status !== 401) {
      throw new Error(result.error ?? `Edge Function error: ${response.status}`);
    }

    // 403 / 401 → fall through to direct insert below
    console.warn('[createStore] Edge Function role check failed, falling back to direct insert:', result.error);
  } catch (fetchErr: any) {
    // Network error (Edge Function not deployed, etc.) → fall through
    if (fetchErr.message && !fetchErr.message.includes('fetch')) {
      throw fetchErr;
    }
    console.warn('[createStore] Edge Function unreachable, falling back to direct insert:', fetchErr.message);
  }

  // ── Attempt 2: Direct insert — relies on ef_is_super_admin() RLS policy ──
  // ef_is_super_admin() has 3 fallback checks (auth_user_id, email, metadata)
  // so this will succeed as long as the migration has been applied.
  const { data: store, error: insertError } = await supabase
    .from('ef_stores')
    .insert([payload])
    .select()
    .single();

  if (insertError) {
    throw new Error(
      insertError.message.includes('row-level security') || insertError.message.includes('policy')
        ? 'Accès refusé : votre compte n\'est pas reconnu comme super administrateur. Vérifiez que votre email est enregistré avec le rôle super_admin dans la base de données.'
        : insertError.message
    );
  }

  auditService.log(
    'store_create',
    `Boutique créée: ${payload.store_name} (${payload.store_slug}) — Propriétaire: ${payload.owner_name} <${payload.owner_email}> — Plan: ${payload.plan}`
  );

  return store as EfStore;
}

export async function updateStore(id: string, payload: Partial<CreateStorePayload>): Promise<EfStore> {
  const { data, error } = await supabase
    .from('ef_stores')
    .update(payload)
    .eq('id', id)
    .select()
    .single();
  if (error) throw error;
  auditService.log(
    'store_update',
    `Boutique modifiée: ID ${id} — Champs: ${Object.keys(payload).join(', ')}`
  );
  return data;
}

export async function deleteStore(id: string): Promise<void> {
  const { data: existing } = await supabase.from('ef_stores').select('store_name, store_slug').eq('id', id).maybeSingle();
  const { error } = await supabase.from('ef_stores').delete().eq('id', id);
  if (error) throw error;
  auditService.log(
    'store_delete',
    `Boutique supprimée: ${existing?.store_name || id} (${existing?.store_slug || ''})`
  );
}

export async function getStoreStats(): Promise<{
  total: number;
  active: number;
  trial: number;
  suspended: number;
  expired: number;
}> {
  const { data, error } = await supabase.from('ef_stores').select('store_status');
  if (error) throw error;
  const rows = data ?? [];
  return {
    total: rows.length,
    active: rows.filter((r) => r.store_status === 'active').length,
    trial: rows.filter((r) => r.store_status === 'trial').length,
    suspended: rows.filter((r) => r.store_status === 'suspended').length,
    expired: rows.filter((r) => r.store_status === 'expired').length,
  };
}
